import { useState, useMemo } from "react";
import { useForm, type SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { type FormValues, schema } from "./schema";
import { STEP, STEP_ORDER, CREDENTIAL_REQUIRED_JOB_TYPE_IDS, type StepValue } from "./constants";

export const useAssessmentForm = (onValidSubmit?: (data: FormValues) => Promise<void> | void) => {
  const stepFields: Record<StepValue, (keyof FormValues)[]> = {
    [STEP.START]: [],
    [STEP.BASIC_INFO]: [
      "sex",
      "birthYear",
      "birthMonth",
      "birthDay",
      "addressPrefecture",
      "addressCity",
    ],
    [STEP.JOB_TYPE]: ["jobType"],
    [STEP.EXPERIENCE]: ["experience"],
    [STEP.CREDENTIALS]: ["credentials"],
    [STEP.MONTHLY_CUSTOMERS]: ["monthlyCustomers"],
    [STEP.MONTHLY_NOMINATED_CUSTOMERS]: ["monthlyNominatedCustomers"],
    [STEP.AVERAGE_SPENDING]: ["averageSpending"],
    [STEP.PERSONAL_SALES]: ["personalSales"],
    [STEP.ACCOUNT]: ["mailAddress", "password"],
  };

  const [currentStep, setCurrentStep] = useState<StepValue>(STEP.START);

  const methods = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      sex: "",
      birthYear: 0,
      birthMonth: 0,
      birthDay: 0,
      addressPrefecture: "",
      addressCity: "",
      jobType: "",
      experience: "",
      credentials: {},
      monthlyCustomers: "",
      monthlyNominatedCustomers: "",
      averageSpending: "",
      personalSales: "",
      mailAddress: "",
      password: "",
    },
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  const { trigger, handleSubmit, watch } = methods;
  const jobType = watch("jobType");

  // 資格選択ステップを表示するかどうかを判定（美容師・アイリストを選択）
  const shouldShowCredentialStep = CREDENTIAL_REQUIRED_JOB_TYPE_IDS.includes(
    jobType as (typeof CREDENTIAL_REQUIRED_JOB_TYPE_IDS)[number],
  );

  // 現在の条件下で表示すべきステップのリストを生成
  const activeSteps = useMemo(() => {
    return STEP_ORDER.filter((step) => {
      // 資格選択ステップは shouldShowCredentialStep に基づいて表示判定
      if (step === STEP.CREDENTIALS) return shouldShowCredentialStep;
      // その他のステップは常に表示
      return true;
    });
  }, [shouldShowCredentialStep]);

  const stepCount = activeSteps.length;

  // 表示用のstepCount（ステップ遷移時にのみ更新される）
  const [displayStepCount, setDisplayStepCount] = useState(stepCount);

  const getNextActiveStep = (step: StepValue): StepValue | null => {
    const currentIndex = activeSteps.indexOf(step);
    if (currentIndex === -1 || currentIndex === activeSteps.length - 1) {
      return null;
    }
    return activeSteps[currentIndex + 1];
  };

  const getPrevActiveStep = (step: StepValue): StepValue | null => {
    const currentIndex = activeSteps.indexOf(step);
    if (currentIndex <= 0) {
      return null;
    }
    return activeSteps[currentIndex - 1];
  };

  const goNext = async () => {
    const fields = stepFields[currentStep];
    const valid = await trigger(fields, { shouldFocus: true });

    if (valid) {
      const nextStep = getNextActiveStep(currentStep);
      if (nextStep !== null) {
        // 次のステップに進む時点でのstepCountを表示用に保存
        setDisplayStepCount(stepCount);
        setCurrentStep(nextStep);
      }
    }
  };

  const goPrev = () => {
    const prevStep = getPrevActiveStep(currentStep);
    if (prevStep !== null) {
      setCurrentStep(prevStep);
    }
  };

  const goToFirstStep = () => {
    setCurrentStep(STEP.BASIC_INFO);
  };

  const goToStepWithErrorFields = (errorFieldNames: (keyof FormValues)[]) => {
    // activeSteps を使用（スキップされたステップをカウントしない）
    const errorStep = activeSteps.find((step) => {
      const fieldsInStep = stepFields[step];
      return fieldsInStep.some((field) => errorFieldNames.includes(field));
    });
    if (errorStep !== undefined) {
      setCurrentStep(errorStep);
    } else {
      goToFirstStep();
    }
  };

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    await onValidSubmit?.(data);
  };

  const currentStepIndex = activeSteps.indexOf(currentStep);

  return {
    methods,
    currentStep,
    currentStepIndex,
    stepCount: displayStepCount,
    goNext,
    goPrev,
    goToFirstStep,
    goToStepWithErrorFields,
    setError: methods.setError,
    handleSubmit: handleSubmit(onSubmit, (formErrors) => {
      goToStepWithErrorFields(Object.keys(formErrors) as (keyof FormValues)[]);
    }),
    isSubmitting: methods.formState.isSubmitting,
  };
};
